import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import ConversionCTA from "../components/ConversionCTA";
import CalculatorCrossLink from "../components/CalculatorCrossLink";
import { ArrowRight, MapPin, FileText, CheckCircle2 } from "lucide-react";
import { PRIORITY_STATES, STATE_SLUGS } from "../lib/constants";

// State-specific copy used on the hub page. Keep these short (~24 words):
// they appear as the body of each state card and feed Google's
// description snippets. Numbers and form names give the cards bite.
const STATE_CARDS = {
  California: {
    accent: "from-amber-50 to-rose-50",
    tagline: "REG 135 + 10-day reporting",
    bullets: [
      "Bill of sale aligned with DMV REG 135 fields",
      "10-day Notice of Transfer & Release of Liability",
      "Smog certificate guidance (counties + exemptions)",
      "Sales tax rules + family-member transfer exemption",
    ],
    description:
      "California is our most detailed cluster — 6 guides covering REG 135, title transfer, release of liability, smog, and as-is sales.",
    image: "https://images.unsplash.com/photo-1663328319236-86eee3352311?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxjYWxpZm9ybmlhJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc3ODQ5NjIyMnww&ixlib=rb-4.1.0&q=85",
  },
  Texas: {
    accent: "from-orange-50 to-amber-50",
    tagline: "Form 130-U + 6.25% SPV tax",
    bullets: [
      "Bill of sale that pairs with Form 130-U at the county tax office",
      "VTR-346 to release seller liability in 30 days",
      "Standard Presumptive Value (SPV) tax explained",
      "Gift transfer between family — $10 gift tax via Form 14-317",
    ],
    description:
      "Texas private sales need a clean bill of sale + Form 130-U + VTR-346. We help with all three plus SPV tax guidance.",
    image: "https://images.unsplash.com/photo-1592190057402-2bf1ee02118d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHx0ZXhhcyUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3Nzg0OTYyMzN8MA&ixlib=rb-4.1.0&q=85",
  },
  Florida: {
    accent: "from-cyan-50 to-emerald-50",
    tagline: "HSMV 82050 + 30-day deadline",
    bullets: [
      "Bill of sale ready for HSMV 82050 title transfer",
      "Notice of Sale (HSMV 82050) to release liability",
      "6% sales tax + county surtax explained",
      "Plates stay with the seller — surrender at FLHSMV",
    ],
    description:
      "Florida transfers go through FLHSMV. Our Florida bill of sale captures everything HSMV 82050 needs.",
    image: "https://images.unsplash.com/photo-1560427791-bcf0b027ff44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxmbG9yaWRhJTIwYmVhY2h8ZW58MHx8fHwxNzc4NDk2MjMzfDA&ixlib=rb-4.1.0&q=85",
  },
  "New York": {
    accent: "from-slate-50 to-blue-50",
    tagline: "MV-912 + DTF-802 sales tax",
    bullets: [
      "MV-912 Vehicle Bill of Sale (the NY DMV form for private sales)",
      "DTF-802 Statement of Transaction for sales tax",
      "MV-82 registration / title application checklist",
      "Plates stay with the seller — surrender to keep insurance clean",
    ],
    description:
      "New York uses the MV-912 for private sales. We generate the MV-912 plus guidance for MV-82, DTF-802, and plate surrender.",
    image: "https://images.unsplash.com/photo-1514565131-fce0801e5785?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxuZXclMjB5b3JrJTIwY2l0eSUyMHNreWxpbmV8ZW58MHx8fHwxNzc4NDk2MjMzfDA&ixlib=rb-4.1.0&q=85",
  },
};

const SITE = "https://www.carsaledoc.com";

export default function ByState() {
  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Car Bill of Sale by US State",
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    numberOfItems: PRIORITY_STATES.length,
    itemListElement: PRIORITY_STATES.map((s, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: `${s} Car Bill of Sale`,
      url: `${SITE}/${STATE_SLUGS[s]}`,
    })),
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
      { "@type": "ListItem", position: 2, name: "By state", item: SITE + "/by-state" },
    ],
  };

  return (
    <Layout>
      <SEO
        title="Car Bill of Sale by US State | DMV-Friendly Generator"
        description="State-specific car bill of sale for California, Texas, Florida and New York. DMV-aligned fields, odometer disclosure, digital signatures, $4.99."
        path="/by-state"
        jsonLd={[itemListLd, breadcrumbLd]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--csd-primary-light)]/40 via-white to-white pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-12 sm:pt-24 sm:pb-16">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--csd-primary)] font-semibold mb-4">
            <MapPin size={14} /> By state
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-4xl">
            Car Bill of Sale, tailored to your state's DMV rules.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[var(--csd-text-secondary)] max-w-3xl leading-relaxed">
            Every state's DMV has its own forms, deadlines and sales tax rules.
            CarSaleDoc builds a bill of sale that pairs with the official paperwork
            in <strong>California, Texas, Florida and New York</strong> — the four states
            with the highest used-car volume in the US. Pick yours below.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/builder/new" data-testid="hero-cta-builder" className="csd-btn-primary px-5 py-3 rounded-xl text-sm font-semibold inline-flex items-center gap-2">
              Start my bill of sale <ArrowRight size={14} />
            </Link>
            <Link to="/sample-pdf" data-testid="hero-cta-sample" className="px-5 py-3 rounded-xl text-sm font-semibold border border-[var(--csd-border)] hover:border-[var(--csd-primary)] inline-flex items-center gap-2">
              <FileText size={14} /> View sample PDF
            </Link>
          </div>
        </div>
      </section>

      {/* STATE GRID */}
      <section className="max-w-7xl mx-auto px-6 pb-12">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-1">Choose your state</h2>
        <p className="text-sm text-[var(--csd-text-secondary)] mb-8">
          Each state page lists the exact DMV forms, deadlines and sales tax rates that apply.
        </p>

        <div className="grid sm:grid-cols-2 gap-6">
          {PRIORITY_STATES.map((stateName) => {
            const c = STATE_CARDS[stateName];
            const to = `/${STATE_SLUGS[stateName]}`;
            return (
              <Link
                key={stateName}
                to={to}
                data-testid={`state-card-${STATE_SLUGS[stateName]}`}
                className={`group relative block rounded-2xl border border-[var(--csd-border)] bg-gradient-to-br ${c.accent} overflow-hidden hover:-translate-y-0.5 hover:shadow-lg transition-all`}
              >
                <div className="absolute inset-0">
                  <img src={c.image} alt={`${stateName} skyline`} className="w-full h-full object-cover opacity-15 group-hover:opacity-25 transition-opacity" loading="lazy" />
                </div>
                <div className="relative p-6 sm:p-7">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[var(--csd-primary)] font-semibold mb-1">{c.tagline}</div>
                      <h3 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">{stateName}</h3>
                    </div>
                    <ArrowRight className="text-[var(--csd-primary)] mt-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="mt-3 text-sm text-[var(--csd-text-secondary)] leading-relaxed">{c.description}</p>
                  <ul className="mt-4 space-y-1.5">
                    {c.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-[var(--csd-text-primary)]">
                        <CheckCircle2 size={14} className="text-[var(--csd-primary)] mt-1 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--csd-primary)]">
                    Open the {stateName} guide <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* OTHER STATES NOTE */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="rounded-2xl border border-[var(--csd-border)] bg-white p-6 sm:p-8">
          <h2 className="font-heading text-xl sm:text-2xl font-bold">In a different state? You can still use CarSaleDoc.</h2>
          <p className="mt-3 text-sm text-[var(--csd-text-secondary)] leading-relaxed max-w-3xl">
            The wizard works in every US state — pick your state during step 2 and the
            generic template adapts to your inputs. State-specific guidance (DMV forms,
            sales tax, plate rules) is currently most detailed for{" "}
            <strong>California, Texas, Florida and New York</strong>, with more states
            on the roadmap. For other states, you'll still get a clean, DMV-friendly
            bill of sale that documents the price, the odometer reading, the parties
            and the as-is terms.
          </p>
          <div className="mt-5">
            <Link to="/builder/new" data-testid="otherstate-cta" className="csd-btn-primary px-5 py-3 rounded-xl text-sm font-semibold inline-flex items-center gap-2">
              Start my bill of sale <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <ConversionCTA />
      <CalculatorCrossLink testId="bystate-calc-crosslink" />
    </Layout>
  );
}
